---
layout: docs
title: "Tools: MCP Connections"
---


MCP (Model Context Protocol) lets you connect agents to external tool servers. When you add an MCP server, its tools become available to the agent alongside built-in tools.

---

## What MCP Is

MCP is an open protocol for connecting AI agents to external tools and data sources. An MCP server exposes a set of tools — each with a name, description, and parameter schema — that an agent can discover and call.

Corral acts as an MCP client. You point it at an MCP server, and the tools that server exposes become available to your agents.

---

## Adding an MCP Server

### Via the Admin Console

1. Navigate to your app in the admin console
2. Open the **Connections** tab
3. Select **MCP**
4. Add a new MCP server:
   - **Name** — a display name for this connection
   - **URL** — the HTTP endpoint of the MCP server
   - **Authentication** — optional basic auth credentials

5. Save. The tools from the MCP server are discovered and available in test immediately.

### Via API

```
POST /mcp/servers
```

MCP server configurations are managed through REST endpoints. See [API Reference →](../extending-corral/api-reference) for details.

---

## How MCP Tools Work at Runtime

When an agent starts a turn:

1. Corral's MCP client factory loads all configured MCP servers for the app
2. It connects to each server and discovers available tools
3. Those tools are added to the agent's tool collection alongside built-in tools
4. The agent can call MCP tools just like any other tool — the protocol handles serialization and transport

MCP tools appear in the Build & Test debug view like any other tool call, so you can inspect what's happening.

---

## Scoping

MCP connections are scoped to a **project and app**. Each app can have its own set of MCP servers. This means:

- A "Support" agent can connect to your ticketing system's MCP server
- A "Marketing" agent can connect to your CMS's MCP server
- Neither sees the other's connections

---

## Test vs. Published

Like all app configuration, MCP connections have test and published states. Adding an MCP server makes it available in the Build & Test environment immediately. Publishing a version snapshots the current MCP configuration for production use.

---

## Supported MCP Features

- HTTP-based MCP servers
- Optional basic authentication
- Tool discovery and invocation

<!-- TODO: Which MCP transport protocols are supported (HTTP SSE, Streamable HTTP, etc.). Whether stdio-based MCP servers are supported. Any limits on the number of MCP servers per app or tools per server. Whether MCP resources (not just tools) are supported. -->

*This section is a work in progress.*

